import React, {
  useRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { colors } from "../../theme/colors";

export interface VariableChipEditorRef {
  insertAtCursor: (text: string) => void;
  getCursorPosition: () => number;
  setCursorPosition: (position: number) => void;
  focus: () => void;
  wrapSelection: (before: string, after: string) => void;
  formatLines: (listType: 'unordered' | 'ordered' | 'blockquote') => void;
}

export interface VariableChipEditorProps {
  value: string;
  onChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  variableLabelMap: Record<string, string>;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  style?: React.CSSProperties;
  className?: string;
}

const VARIABLE_PATTERN = /\{\{([a-z_][a-z0-9_]*)\}\}/g;

const CHIP_STYLES: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  backgroundColor: colors.primaryExtraLight,
  color: colors.primary,
  padding: "1px 5px",
  borderRadius: "4px",
  fontSize: "0.85em",
  fontWeight: 500,
  lineHeight: "inherit",
  cursor: "grab",
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
  transition: "opacity 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
};

const CHIP_STYLE_STRING = Object.entries(CHIP_STYLES)
  .map(([key, val]) => {
    const cssKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
    return `${cssKey}:${val}`;
  })
  .join(";");

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Collect all text nodes within a container, optionally skipping nodes
 * that are inside chip elements (data-var spans).
 */
function collectTextNodes(
  container: HTMLElement,
  skipChipDescendants: boolean,
): Text[] {
  const filter = skipChipDescendants
    ? {
        acceptNode(node: Node): number {
          let parent = (node as Text).parentElement;
          while (parent && parent !== container) {
            if (parent.hasAttribute("data-var")) return NodeFilter.FILTER_REJECT;
            parent = parent.parentElement;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      }
    : null;

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    filter,
  );
  const nodes: Text[] = [];
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    nodes.push(node);
  }
  return nodes;
}

/**
 * Create a chip DOM element for a variable.
 */
function createChipElement(varName: string, label: string): HTMLSpanElement {
  const chip = document.createElement("span");
  chip.setAttribute("data-var", varName);
  chip.setAttribute("contenteditable", "false");
  chip.setAttribute("draggable", "true");
  Object.assign(chip.style, CHIP_STYLES);
  chip.textContent = label;
  return chip;
}

/**
 * Place the browser cursor immediately after the given node.
 */
function placeCursorAfter(node: Node): void {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Ensure the editor has a valid selection. If no selection exists,
 * focus the container and place cursor at the end.
 * Returns the current Selection object.
 */
function ensureSelection(container: HTMLElement): Selection {
  let sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) {
    container.focus();
    sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(container);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }
  return sel!;
}

/**
 * Convert a plain text value (with {{var_name}} patterns) to HTML with chip spans.
 */
function valueToHtml(
  value: string,
  labelMap: Record<string, string>,
): string {
  if (!value) return "";

  const parts: string[] = [];
  let lastIndex = 0;
  const regex = new RegExp(VARIABLE_PATTERN.source, "g");
  let match: RegExpExecArray | null;

  while ((match = regex.exec(value)) !== null) {
    if (match.index > lastIndex) {
      parts.push(escapeHtml(value.substring(lastIndex, match.index)));
    }

    const varName = match[1];
    const label = labelMap[varName] || varName;

    parts.push(
      `<span data-var="${escapeHtml(varName)}" contenteditable="false" draggable="true" style="${CHIP_STYLE_STRING}">${escapeHtml(label)}</span>`,
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < value.length) {
    parts.push(escapeHtml(value.substring(lastIndex)));
  }

  return parts.join("").replace(/\n/g, "<br>");
}

/**
 * Walk a DOM tree and build a value string, converting chip elements
 * to {{var_name}} syntax and BR/block elements to newlines.
 */
function walkNodesToValue(parent: Node): string {
  let result = "";

  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent || "";
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;

      const varName = el.getAttribute("data-var");
      if (varName) {
        result += `{{${varName}}}`;
        return;
      }

      if (el.tagName === "BR") {
        result += "\n";
        return;
      }

      const isBlock = el.tagName === "DIV" || el.tagName === "P";
      if (isBlock && result.length > 0 && !result.endsWith("\n")) {
        result += "\n";
      }

      for (let i = 0; i < node.childNodes.length; i++) {
        walk(node.childNodes[i]);
      }
    }
  }

  for (let i = 0; i < parent.childNodes.length; i++) {
    walk(parent.childNodes[i]);
  }

  return result;
}

/**
 * Convert HTML from the contentEditable div back to plain text value.
 */
function htmlToValue(container: HTMLElement): string {
  let result = walkNodesToValue(container);

  if (result.endsWith("\n")) {
    result = result.slice(0, -1);
  }

  return result.replace(/\r/g, "");
}

/**
 * Check if a node is a variable chip element.
 */
function isChipNode(node: Node | null): boolean {
  return !!node && node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).hasAttribute("data-var");
}

/**
 * Ensure proper spacing around a chip element.
 * Adds a single space before the chip if preceded by another chip,
 * and a single trailing space after for comfortable typing.
 * Collapses any excess whitespace around the chip.
 * Returns the trailing space node for cursor placement.
 */
function ensureChipSpacing(chip: HTMLElement): Text {
  // Space before chip if previous sibling is a chip
  const prev = chip.previousSibling;
  if (isChipNode(prev)) {
    chip.parentNode!.insertBefore(document.createTextNode(" "), chip);
  } else if (prev && prev.nodeType === Node.TEXT_NODE && prev.textContent) {
    // Collapse trailing whitespace on previous text node to a single space
    prev.textContent = prev.textContent.replace(/\s+$/, " ");
  }

  // Always ensure a single space after the chip
  const next = chip.nextSibling;
  let trailingSpace: Text;

  if (!next) {
    trailingSpace = document.createTextNode(" ");
    chip.parentNode!.appendChild(trailingSpace);
  } else if (isChipNode(next)) {
    trailingSpace = document.createTextNode(" ");
    chip.parentNode!.insertBefore(trailingSpace, next);
  } else if (next.nodeType === Node.TEXT_NODE) {
    // Collapse leading whitespace on following text node to a single space
    if (next.textContent) {
      next.textContent = next.textContent.replace(/^\s+/, " ");
    }
    trailingSpace = next as Text;
    // If the text node is now empty or only spaces, ensure at least one space
    if (!trailingSpace.textContent || trailingSpace.textContent.length === 0) {
      trailingSpace.textContent = " ";
    }
  } else {
    trailingSpace = document.createTextNode(" ");
    chip.parentNode!.insertBefore(trailingSpace, next);
  }

  return trailingSpace;
}

/**
 * Clean up excess whitespace in the editor after drag-and-drop.
 * Collapses multiple consecutive spaces into single spaces
 * and removes orphaned empty text nodes.
 */
function cleanupWhitespace(container: HTMLElement): void {
  for (const textNode of collectTextNodes(container, false)) {
    if (!textNode.textContent) continue;
    const cleaned = textNode.textContent.replace(/ {2,}/g, " ");
    if (cleaned !== textNode.textContent) {
      textNode.textContent = cleaned;
    }
    if (textNode.textContent === "" && textNode.parentNode) {
      textNode.parentNode.removeChild(textNode);
    }
  }
}

/**
 * Remove all partial-variable highlight spans, unwrapping their text content.
 */
function removePartialHighlights(container: HTMLElement): void {
  const spans = container.querySelectorAll("[data-partial-var]");
  if (spans.length === 0) return;
  spans.forEach((span) => {
    const parent = span.parentNode;
    if (!parent) return;
    while (span.firstChild) {
      parent.insertBefore(span.firstChild, span);
    }
    parent.removeChild(span);
  });
  container.normalize();
}

/**
 * Scan text nodes for complete {{var_name}} patterns typed inline and
 * replace them with chip elements. Returns true if a conversion occurred.
 */
function convertInlineVariablesToChips(
  container: HTMLElement,
  labelMap: Record<string, string>,
): boolean {
  for (const textNode of collectTextNodes(container, true)) {
    const text = textNode.textContent || "";
    const match = new RegExp(VARIABLE_PATTERN.source).exec(text);
    if (!match) continue;

    const varName = match[1];
    const label = labelMap[varName] || varName;
    const beforeText = text.substring(0, match.index);
    const afterText = text.substring(match.index + match[0].length);
    const parent = textNode.parentNode!;

    const chip = createChipElement(varName, label);

    if (beforeText) {
      parent.insertBefore(document.createTextNode(beforeText), textNode);
    }
    parent.insertBefore(chip, textNode);
    if (afterText) {
      textNode.textContent = afterText;
    } else {
      parent.removeChild(textNode);
    }

    const trailingSpace = ensureChipSpacing(chip);
    placeCursorAfter(trailingSpace);

    return true;
  }

  return false;
}

/**
 * Highlight partial {{... patterns with a subtle background.
 * Matches: {{, {{name, {{name} (but not complete {{name}}).
 */
function highlightPartialVariables(container: HTMLElement): void {
  const partialPattern = /\{\{([a-z_][a-z0-9_]*)?\}?$/;

  for (const textNode of collectTextNodes(container, true)) {
    const text = textNode.textContent || "";
    if (new RegExp(VARIABLE_PATTERN.source).test(text)) continue;

    const match = partialPattern.exec(text);
    if (!match) continue;

    const sel = window.getSelection();
    let cursorOffset = -1;
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (range.startContainer === textNode) {
        cursorOffset = range.startOffset;
      }
    }

    const matchStart = match.index;
    const beforeText = text.substring(0, matchStart);
    const highlightText = text.substring(matchStart);
    const parent = textNode.parentNode!;

    if (beforeText) {
      parent.insertBefore(document.createTextNode(beforeText), textNode);
    }

    const highlightSpan = document.createElement("span");
    highlightSpan.setAttribute("data-partial-var", "true");
    highlightSpan.textContent = highlightText;
    parent.insertBefore(highlightSpan, textNode);
    parent.removeChild(textNode);

    if (cursorOffset >= 0 && sel && highlightSpan.firstChild) {
      const newOffset = cursorOffset - matchStart;
      if (newOffset >= 0 && newOffset <= highlightText.length) {
        const range = document.createRange();
        range.setStart(highlightSpan.firstChild, newOffset);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }

    return;
  }
}

/**
 * Insert a chip span element at the current cursor position,
 * ensuring proper spacing and placing cursor after the chip.
 */
function insertChipAtCursor(
  container: HTMLElement,
  varName: string,
  label: string,
): void {
  const sel = ensureSelection(container);
  const range = sel.getRangeAt(0);

  const chip = createChipElement(varName, label);
  range.deleteContents();
  range.insertNode(chip);

  const trailingSpace = ensureChipSpacing(chip);
  placeCursorAfter(trailingSpace);
}

/**
 * Insert plain text at the current cursor position.
 */
function insertTextNodeAtCursor(
  container: HTMLElement,
  text: string,
): void {
  const sel = ensureSelection(container);
  const range = sel.getRangeAt(0);
  range.deleteContents();

  const textNode = document.createTextNode(text);
  range.insertNode(textNode);
  placeCursorAfter(textNode);
}

/**
 * Cross-browser caret range from mouse coordinates.
 */
function getCaretRangeFromPoint(x: number, y: number): Range | null {
  if (document.caretRangeFromPoint) {
    return document.caretRangeFromPoint(x, y);
  }
  // Firefox fallback
  const caretPos = (document as unknown as { caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null }).caretPositionFromPoint?.(x, y);
  if (caretPos) {
    const range = document.createRange();
    range.setStart(caretPos.offsetNode, caretPos.offset);
    range.collapse(true);
    return range;
  }
  return null;
}

/**
 * Extract the currently selected content as a plain text value,
 * converting chip elements back to {{var_name}} syntax.
 * Returns null if there's no valid selection.
 */
function getSelectionAsValue(container: HTMLElement): string | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;

  const range = sel.getRangeAt(0);
  if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) return null;

  const fragment = range.cloneContents();
  return walkNodesToValue(fragment);
}

/**
 * Get the value-string length of a DOM node.
 * Text nodes → text length, chips → {{name}} length, BR → 1.
 */
function nodeValueLength(node: Node): number {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent || "").length;
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement;
    if (el.hasAttribute("data-var")) {
      return (el.getAttribute("data-var") || "").length + 4;
    }
    if (el.tagName === "BR") return 1;
    let len = 0;
    for (let i = 0; i < node.childNodes.length; i++) {
      len += nodeValueLength(node.childNodes[i]);
    }
    return len;
  }
  return 0;
}

/**
 * Convert a DOM position (node + offset) to a value-string offset.
 * Walks the DOM tree counting value characters until reaching the target.
 */
function domToValueOffset(
  container: HTMLElement,
  targetNode: Node,
  targetOffset: number,
): number {
  if (targetNode === container) {
    let total = 0;
    for (let i = 0; i < targetOffset && i < container.childNodes.length; i++) {
      total += nodeValueLength(container.childNodes[i]);
    }
    return total;
  }

  let pos = 0;
  let found = false;

  function visit(node: Node): void {
    if (found) return;
    if (node === targetNode) {
      found = true;
      if (node.nodeType === Node.TEXT_NODE) {
        pos += targetOffset;
      } else {
        for (let i = 0; i < targetOffset && i < node.childNodes.length; i++) {
          pos += nodeValueLength(node.childNodes[i]);
        }
      }
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      pos += (node.textContent || "").length;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.hasAttribute("data-var")) {
        pos += (el.getAttribute("data-var") || "").length + 4;
      } else if (el.tagName === "BR") {
        pos += 1;
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          visit(node.childNodes[i]);
          if (found) return;
        }
      }
    }
  }

  for (let i = 0; i < container.childNodes.length; i++) {
    visit(container.childNodes[i]);
    if (found) break;
  }

  return pos;
}

/**
 * Convert a value-string offset to a DOM position (node + offset).
 * Used to place the cursor after re-rendering from a value string.
 */
function valueOffsetToDom(
  container: HTMLElement,
  targetOffset: number,
): { node: Node; offset: number } {
  let remaining = targetOffset;

  function visit(node: Node): { node: Node; offset: number } | null {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = (node.textContent || "").length;
      if (remaining <= len) {
        return { node, offset: remaining };
      }
      remaining -= len;
      return null;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.hasAttribute("data-var")) {
        const len = (el.getAttribute("data-var") || "").length + 4;
        const parent = el.parentNode!;
        const idx = Array.from(parent.childNodes).indexOf(el as ChildNode);
        if (remaining <= 0) {
          return { node: parent, offset: idx };
        }
        if (remaining <= len) {
          return { node: parent, offset: idx + 1 };
        }
        remaining -= len;
        return null;
      }
      if (el.tagName === "BR") {
        if (remaining <= 1) {
          const parent = el.parentNode!;
          const idx = Array.from(parent.childNodes).indexOf(el as ChildNode);
          return { node: parent, offset: idx + (remaining === 1 ? 1 : 0) };
        }
        remaining -= 1;
        return null;
      }
      for (let i = 0; i < node.childNodes.length; i++) {
        const result = visit(node.childNodes[i]);
        if (result) return result;
      }
    }
    return null;
  }

  for (let i = 0; i < container.childNodes.length; i++) {
    const result = visit(container.childNodes[i]);
    if (result) return result;
  }

  return { node: container, offset: container.childNodes.length };
}

/**
 * CSS styles injected for chip hover/drag effects, scoped to the editor.
 */
const EDITOR_STYLES = `
  .chip-editor-container [data-var]:hover {
    box-shadow: 0 1px 3px rgba(0,61,138,0.2);
    transform: translateY(-0.5px);
  }
  .chip-editor-container [data-var].chip-dragging {
    opacity: 0.25 !important;
    cursor: grabbing !important;
    transform: scale(0.95) !important;
    box-shadow: none !important;
    transition: opacity 0.15s ease, transform 0.15s ease !important;
  }
  .chip-editor-container [data-var]:active {
    cursor: grabbing;
  }
  .chip-drop-indicator {
    position: absolute;
    width: 2px;
    background-color: ${colors.primary};
    border-radius: 1px;
    pointer-events: none;
    z-index: 10;
    transition: left 0.1s ease-out, top 0.1s ease-out, opacity 0.1s ease;
    box-shadow: 0 0 4px ${colors.primary}40;
  }
  .chip-drop-indicator::before,
  .chip-drop-indicator::after {
    content: '';
    position: absolute;
    left: -2px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${colors.primary};
  }
  .chip-drop-indicator::before {
    top: -3px;
  }
  .chip-drop-indicator::after {
    bottom: -3px;
  }
  .chip-editor-container [data-partial-var] {
    background-color: ${colors.primaryExtraLight};
    border-radius: 2px;
    padding: 0 1px;
  }
  .chip-editor-container [data-var].chip-in-selection {
    background-color: ${colors.primary} !important;
    color: #ffffff !important;
    box-shadow: none !important;
    transform: none !important;
  }
`;

export const VariableChipEditor = forwardRef<
  VariableChipEditorRef,
  VariableChipEditorProps
>(
  (
    {
      value,
      onChange,
      onFocus,
      onBlur,
      variableLabelMap,
      placeholder,
      disabled = false,
      maxLength,
      style,
      className,
    },
    ref,
  ) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const dropIndicatorRef = useRef<HTMLDivElement>(null);
    const lastValueRef = useRef<string>(value);
    const isInternalChangeRef = useRef(false);
    const lastCursorPositionRef = useRef<number>(0);
    const savedRangeRef = useRef<Range | null>(null);
    const draggedChipRef = useRef<HTMLElement | null>(null);
    const isDraggingRef = useRef(false);
    const styleInjectedRef = useRef(false);
    const isConvertingRef = useRef(false);
    // Prevents the variableLabelMap effect from re-rendering innerHTML
    // immediately after an insert (which would destroy cursor position)
    const skipNextLabelRenderRef = useRef(false);

    // Inject scoped CSS styles once
    useEffect(() => {
      if (styleInjectedRef.current) return;
      styleInjectedRef.current = true;
      const styleEl = document.createElement("style");
      styleEl.textContent = EDITOR_STYLES;
      styleEl.setAttribute("data-chip-editor-styles", "true");
      if (!document.querySelector("[data-chip-editor-styles]")) {
        document.head.appendChild(styleEl);
      }
    }, []);

    // Sync HTML from value only on EXTERNAL value changes
    useEffect(() => {
      if (isInternalChangeRef.current) {
        isInternalChangeRef.current = false;
        return;
      }

      const editor = editorRef.current;
      if (!editor) return;

      if (value !== lastValueRef.current) {
        lastValueRef.current = value;
        const html = valueToHtml(value, variableLabelMap);
        editor.innerHTML = html;
        savedRangeRef.current = null;
      }
    }, [value, variableLabelMap]);

    // Initial render
    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;
      const html = valueToHtml(value, variableLabelMap);
      editor.innerHTML = html;
      lastValueRef.current = value;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-render chips when labelMap changes (e.g. labels resolved from API)
    // BUT skip if we just did an insert — the chip was already created with
    // the correct label, and re-rendering would destroy the cursor position.
    useEffect(() => {
      if (skipNextLabelRenderRef.current) {
        skipNextLabelRenderRef.current = false;
        return;
      }
      const editor = editorRef.current;
      if (!editor) return;
      const html = valueToHtml(lastValueRef.current, variableLabelMap);
      editor.innerHTML = html;
      savedRangeRef.current = null;
    }, [variableLabelMap]);

    const extractAndNotify = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return;

      const newValue = htmlToValue(editor);
      if (maxLength && newValue.length > maxLength) return;

      lastValueRef.current = newValue;
      isInternalChangeRef.current = true;
      onChange?.(newValue);
    }, [onChange, maxLength]);

    const handleInput = useCallback(() => {
      if (isDraggingRef.current || isConvertingRef.current) return;

      const editor = editorRef.current;
      if (!editor) return;

      isConvertingRef.current = true;

      // Remove old partial highlights before processing
      removePartialHighlights(editor);

      // Convert any complete {{var}} patterns to chips
      let didConvert = false;
      while (convertInlineVariablesToChips(editor, variableLabelMap)) {
        didConvert = true;
        editor.normalize();
      }
      if (didConvert) {
        cleanupWhitespace(editor);
      }

      // Highlight partial {{ patterns for visual feedback
      highlightPartialVariables(editor);

      isConvertingRef.current = false;

      extractAndNotify();
    }, [extractAndNotify, variableLabelMap]);

    const handlePaste = useCallback(
      (e: React.ClipboardEvent) => {
        e.preventDefault();
        let text = e.clipboardData.getData("text/plain");
        if (!text) return;

        // Normalize Windows/old-Mac line endings to Unix \n
        text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

        const editor = editorRef.current;
        if (!editor) return;

        // Enforce maxLength: truncate pasted text to fit within the limit
        if (maxLength) {
          const currentValue = htmlToValue(editor);
          const sel = window.getSelection();
          const selectedLength =
            sel && !sel.isCollapsed && sel.rangeCount > 0
              ? (getSelectionAsValue(editor) || "").length
              : 0;
          const availableSpace =
            maxLength - (currentValue.length - selectedLength);
          if (availableSpace <= 0) return;
          if (text.length > availableSpace) {
            text = text.substring(0, availableSpace);
          }
        }

        const regex = new RegExp(VARIABLE_PATTERN.source, "g");
        let match: RegExpExecArray | null;
        let lastIdx = 0;
        const fragments: Array<{ type: "text" | "var"; content: string }> = [];

        while ((match = regex.exec(text)) !== null) {
          if (match.index > lastIdx) {
            fragments.push({ type: "text", content: text.substring(lastIdx, match.index) });
          }
          fragments.push({ type: "var", content: match[1] });
          lastIdx = match.index + match[0].length;
        }
        if (lastIdx < text.length) {
          fragments.push({ type: "text", content: text.substring(lastIdx) });
        }

        if (fragments.some((f) => f.type === "var")) {
          for (const fragment of fragments) {
            if (fragment.type === "var") {
              const label = variableLabelMap[fragment.content] || fragment.content;
              insertChipAtCursor(editor, fragment.content, label);
            } else {
              insertTextNodeAtCursor(editor, fragment.content);
            }
          }
          // Merge adjacent text nodes and collapse double spaces
          // caused by ensureChipSpacing + original text spacing
          editor.normalize();
          cleanupWhitespace(editor);
        } else {
          insertTextNodeAtCursor(editor, text);
        }

        extractAndNotify();
      },
      [extractAndNotify, variableLabelMap],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (maxLength) {
          const editor = editorRef.current;
          if (!editor) return;
          const currentValue = htmlToValue(editor);
          if (
            currentValue.length >= maxLength &&
            !e.ctrlKey &&
            !e.metaKey &&
            e.key.length === 1 &&
            !window.getSelection()?.toString()
          ) {
            e.preventDefault();
          }
        }
      },
      [maxLength],
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLDivElement>) => {
        onFocus?.(e);
      },
      [onFocus],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLDivElement>) => {
        // Save the current selection Range for later restoration
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const editor = editorRef.current;
          if (editor) {
            const range = sel.getRangeAt(0);
            if (editor.contains(range.startContainer)) {
              savedRangeRef.current = range.cloneRange();
            }
            const preRange = document.createRange();
            preRange.selectNodeContents(editor);
            preRange.setEnd(range.startContainer, range.startOffset);
            lastCursorPositionRef.current = preRange.toString().length;
          }
        }
        onBlur?.(e);
      },
      [onBlur],
    );

    /**
     * Restore the saved selection range from before blur.
     * Used by insertAtCursor and wrapSelection to place the cursor
     * at the correct position after the editor regains focus.
     */
    const restoreSavedRange = useCallback(() => {
      const editor = editorRef.current;
      if (!editor || !savedRangeRef.current) return;

      const sel = window.getSelection();
      if (sel) {
        try {
          if (editor.contains(savedRangeRef.current.startContainer)) {
            sel.removeAllRanges();
            sel.addRange(savedRangeRef.current);
          }
        } catch {
          // Range is stale (DOM was re-rendered), cursor stays where focus() placed it
        }
      }
      savedRangeRef.current = null;
    }, []);

    // ─── Copy & Cut ──────────────────────────────────────────────────

    const handleCopy = useCallback((e: React.ClipboardEvent) => {
      const editor = editorRef.current;
      if (!editor) return;

      const selectedText = getSelectionAsValue(editor);
      if (selectedText !== null) {
        e.preventDefault();
        e.clipboardData.setData("text/plain", selectedText);
      }
    }, []);

    const handleCut = useCallback((e: React.ClipboardEvent) => {
      const editor = editorRef.current;
      if (!editor) return;

      const selectedText = getSelectionAsValue(editor);
      if (selectedText !== null) {
        e.preventDefault();
        e.clipboardData.setData("text/plain", selectedText);

        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          sel.getRangeAt(0).deleteContents();
          editor.normalize();
          cleanupWhitespace(editor);
        }

        extractAndNotify();
      }
    }, [extractAndNotify]);

    // ─── Drag and Drop ─────────────────────────────────────────────────

    const showDropIndicator = useCallback((x: number, y: number) => {
      const editor = editorRef.current;
      const indicator = dropIndicatorRef.current;
      if (!editor || !indicator) return;

      const wrapperRect = editor.parentElement!.getBoundingClientRect();

      // Check if hovering over a chip (non-editable elements need special handling)
      const elementAtPoint = document.elementFromPoint(x, y);
      const targetChip = elementAtPoint?.closest?.("[data-var]") as HTMLElement | null;

      if (targetChip && targetChip !== draggedChipRef.current && editor.contains(targetChip)) {
        const chipRect = targetChip.getBoundingClientRect();
        const midX = chipRect.left + chipRect.width / 2;
        const indicatorLeft = x < midX
          ? chipRect.left - wrapperRect.left - 1
          : chipRect.right - wrapperRect.left + 1;

        indicator.style.display = "block";
        indicator.style.left = `${indicatorLeft}px`;
        indicator.style.top = `${chipRect.top - wrapperRect.top}px`;
        indicator.style.height = `${chipRect.height}px`;
        return;
      }

      // Regular text position
      const range = getCaretRangeFromPoint(x, y);
      if (!range || !editor.contains(range.startContainer)) {
        indicator.style.display = "none";
        return;
      }

      if (draggedChipRef.current && draggedChipRef.current.contains(range.startContainer)) {
        indicator.style.display = "none";
        return;
      }

      const rangeRect = range.getBoundingClientRect();
      const height = rangeRect.height > 0 ? rangeRect.height : 20;
      const left = rangeRect.left - wrapperRect.left;
      const top = rangeRect.top - wrapperRect.top;

      if (rangeRect.left === 0 && rangeRect.top === 0 && rangeRect.height === 0) {
        indicator.style.display = "none";
        return;
      }

      indicator.style.display = "block";
      indicator.style.left = `${left}px`;
      indicator.style.top = `${top}px`;
      indicator.style.height = `${height}px`;
    }, []);

    const hideDropIndicator = useCallback(() => {
      const indicator = dropIndicatorRef.current;
      if (indicator) {
        indicator.style.display = "none";
      }
    }, []);

    // Set up native drag event listeners on the editor
    useEffect(() => {
      const editor = editorRef.current;
      if (!editor || disabled) return;

      const handleDragStart = (e: DragEvent) => {
        const targetEl = e.target instanceof HTMLElement
          ? e.target
          : (e.target as Node).parentElement;
        const chip = targetEl?.closest?.("[data-var]") as HTMLElement | null;
        if (!chip || !editor.contains(chip)) return;

        isDraggingRef.current = true;
        draggedChipRef.current = chip;

        e.dataTransfer!.effectAllowed = "move";
        e.dataTransfer!.setData("text/plain", chip.getAttribute("data-var") || "");

        try {
          e.dataTransfer!.setDragImage(chip, chip.offsetWidth / 2, chip.offsetHeight / 2);
        } catch {
          // Some browsers may not support setDragImage
        }

        chip.classList.add("chip-dragging");
      };

      const handleDragOver = (e: DragEvent) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();
        e.dataTransfer!.dropEffect = "move";
        showDropIndicator(e.clientX, e.clientY);
      };

      const handleDragLeave = (e: DragEvent) => {
        if (!isDraggingRef.current) return;
        const relatedTarget = e.relatedTarget as Node | null;
        if (!relatedTarget || !editor.contains(relatedTarget)) {
          hideDropIndicator();
        }
      };

      const handleDrop = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const chip = draggedChipRef.current;
        if (!chip || !isDraggingRef.current) return;

        hideDropIndicator();

        // Determine drop position: check if dropping onto another chip
        const elementAtPoint = document.elementFromPoint(e.clientX, e.clientY);
        const targetChip = elementAtPoint?.closest?.("[data-var]") as HTMLElement | null;

        if (targetChip && targetChip !== chip && editor.contains(targetChip)) {
          // Dropping on/near another chip — place before or after it based on mouse X
          const chipRect = targetChip.getBoundingClientRect();
          const midX = chipRect.left + chipRect.width / 2;

          chip.remove();

          if (e.clientX < midX) {
            targetChip.parentNode!.insertBefore(chip, targetChip);
          } else {
            targetChip.parentNode!.insertBefore(chip, targetChip.nextSibling);
          }
        } else {
          // Dropping on text — use caret position
          const dropRange = getCaretRangeFromPoint(e.clientX, e.clientY);
          if (!dropRange || !editor.contains(dropRange.startContainer)) {
            cleanupDrag();
            return;
          }

          // Don't drop onto the chip itself
          if (chip.contains(dropRange.startContainer)) {
            cleanupDrag();
            return;
          }

          // Check if dropping at same position
          if (dropRange.startContainer === chip.parentNode) {
            const chipIndex = Array.from(chip.parentNode!.childNodes).indexOf(chip as ChildNode);
            if (dropRange.startOffset === chipIndex || dropRange.startOffset === chipIndex + 1) {
              cleanupDrag();
              return;
            }
          }

          // Use a comment marker to preserve drop position across DOM removal
          const marker = document.createComment("drop-marker");
          dropRange.insertNode(marker);

          chip.remove();
          marker.parentNode!.insertBefore(chip, marker);
          marker.remove();
        }

        // Ensure proper spacing around the moved chip
        ensureChipSpacing(chip);

        // Normalize adjacent text nodes and clean up excess whitespace
        editor.normalize();
        cleanupWhitespace(editor);

        // Place cursor after the chip + its trailing space
        placeCursorAfter(chip.nextSibling || chip);

        cleanupDrag();
        extractAndNotify();
      };

      const handleDragEnd = () => {
        cleanupDrag();
        hideDropIndicator();
      };

      const cleanupDrag = () => {
        if (draggedChipRef.current) {
          draggedChipRef.current.classList.remove("chip-dragging");
          draggedChipRef.current.style.opacity = "";
        }
        draggedChipRef.current = null;
        isDraggingRef.current = false;
      };

      editor.addEventListener("dragstart", handleDragStart);
      editor.addEventListener("dragover", handleDragOver);
      editor.addEventListener("dragleave", handleDragLeave);
      editor.addEventListener("drop", handleDrop);
      editor.addEventListener("dragend", handleDragEnd);

      return () => {
        editor.removeEventListener("dragstart", handleDragStart);
        editor.removeEventListener("dragover", handleDragOver);
        editor.removeEventListener("dragleave", handleDragLeave);
        editor.removeEventListener("drop", handleDrop);
        editor.removeEventListener("dragend", handleDragEnd);
      };
    }, [disabled, extractAndNotify, showDropIndicator, hideDropIndicator]);

    // Track selection changes and highlight chips that are within the selection
    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;

      const updateChipSelection = () => {
        const chips = editor.querySelectorAll("[data-var]");
        const sel = window.getSelection();

        if (
          !sel ||
          sel.rangeCount === 0 ||
          sel.isCollapsed ||
          !editor.contains(sel.anchorNode)
        ) {
          chips.forEach((chip) => chip.classList.remove("chip-in-selection"));
          return;
        }

        const range = sel.getRangeAt(0);

        chips.forEach((chip) => {
          if (range.intersectsNode(chip)) {
            chip.classList.add("chip-in-selection");
          } else {
            chip.classList.remove("chip-in-selection");
          }
        });
      };

      document.addEventListener("selectionchange", updateChipSelection);

      return () => {
        document.removeEventListener("selectionchange", updateChipSelection);
        const chips = editor.querySelectorAll("[data-var].chip-in-selection");
        chips.forEach((chip) => chip.classList.remove("chip-in-selection"));
      };
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        insertAtCursor: (text: string) => {
          const editor = editorRef.current;
          if (!editor) return;

          // Signal that the upcoming variableLabelMap effect should NOT
          // re-render innerHTML (the chip is already being inserted with
          // the correct label, and re-rendering would destroy cursor position).
          skipNextLabelRenderRef.current = true;

          // Clean up any partial highlights before inserting
          removePartialHighlights(editor);

          editor.focus();
          restoreSavedRange();

          const varMatch = text.match(/^\{\{([a-z_][a-z0-9_]*)\}\}$/);
          if (varMatch) {
            const varName = varMatch[1];
            const label = variableLabelMap[varName] || varName;
            insertChipAtCursor(editor, varName, label);
          } else {
            insertTextNodeAtCursor(editor, text);
          }

          extractAndNotify();
        },
        getCursorPosition: () => {
          return lastCursorPositionRef.current;
        },
        setCursorPosition: (position: number) => {
          lastCursorPositionRef.current = position;
          const editor = editorRef.current;
          if (!editor) return;
          editor.focus();
          const sel = window.getSelection();
          if (sel) {
            const range = document.createRange();
            range.selectNodeContents(editor);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        },
        focus: () => {
          editorRef.current?.focus();
        },
        wrapSelection: (before: string, after: string) => {
          const editor = editorRef.current;
          if (!editor) return;

          editor.focus();
          restoreSavedRange();

          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return;

          const range = sel.getRangeAt(0);
          if (!editor.contains(range.startContainer)) return;

          // Convert DOM selection to value-string positions
          const startPos = domToValueOffset(editor, range.startContainer, range.startOffset);
          const endPos = sel.isCollapsed
            ? startPos
            : domToValueOffset(editor, range.endContainer, range.endOffset);

          const currentVal = lastValueRef.current;

          // Build new value with formatting markers
          const newValue =
            currentVal.substring(0, startPos) +
            before +
            currentVal.substring(startPos, endPos) +
            after +
            currentVal.substring(endPos);

          // Cursor: between markers if collapsed, after closing marker if selection
          const cursorPos = sel.isCollapsed
            ? startPos + before.length
            : endPos + before.length + after.length;

          // Update value and re-render
          lastValueRef.current = newValue;
          isInternalChangeRef.current = true;
          onChange?.(newValue);

          editor.innerHTML = valueToHtml(newValue, variableLabelMap);

          // Restore cursor at calculated position
          const domPos = valueOffsetToDom(editor, cursorPos);
          const cursorRange = document.createRange();
          cursorRange.setStart(domPos.node, domPos.offset);
          cursorRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(cursorRange);
        },
        formatLines: (listType: 'unordered' | 'ordered' | 'blockquote') => {
          const editor = editorRef.current;
          if (!editor) return;

          editor.focus();
          restoreSavedRange();

          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return;

          const range = sel.getRangeAt(0);
          if (!editor.contains(range.startContainer)) return;

          const startPos = domToValueOffset(editor, range.startContainer, range.startOffset);
          const endPos = sel.isCollapsed
            ? startPos
            : domToValueOffset(editor, range.endContainer, range.endOffset);

          const currentVal = lastValueRef.current;

          // Expand selection to encompass full lines
          let lineStart = startPos;
          while (lineStart > 0 && currentVal[lineStart - 1] !== '\n') {
            lineStart--;
          }
          let lineEnd = endPos;
          while (lineEnd < currentVal.length && currentVal[lineEnd] !== '\n') {
            lineEnd++;
          }

          const textBefore = currentVal.substring(0, lineStart);
          const selectedArea = currentVal.substring(lineStart, lineEnd);
          const textAfter = currentVal.substring(lineEnd);

          const lines = selectedArea.split('\n');

          const UNORDERED_RE = /^• /;
          const ORDERED_RE = /^\d+\. /;
          const BLOCKQUOTE_RE = /^> /;

          const getPattern = (type: string) => {
            if (type === 'unordered') return UNORDERED_RE;
            if (type === 'ordered') return ORDERED_RE;
            return BLOCKQUOTE_RE;
          };

          const pattern = getPattern(listType);
          const nonEmptyLines = lines.filter(l => l.trim() !== '');
          const allHavePrefix = nonEmptyLines.length > 0 &&
            nonEmptyLines.every(l => pattern.test(l));

          let formattedLines: string[];

          if (allHavePrefix) {
            // Toggle off: remove prefixes
            formattedLines = lines.map(line => {
              if (line.trim() === '') return line;
              return line.replace(pattern, '');
            });
          } else {
            // Add prefixes
            let orderNumber = 1;
            formattedLines = lines.map(line => {
              if (line.trim() === '') return line;

              // Remove any existing list prefix first
              let cleanLine = line;
              if (UNORDERED_RE.test(line)) cleanLine = line.replace(UNORDERED_RE, '');
              else if (ORDERED_RE.test(line)) cleanLine = line.replace(ORDERED_RE, '');
              else if (BLOCKQUOTE_RE.test(line)) cleanLine = line.replace(BLOCKQUOTE_RE, '');

              let prefix: string;
              if (listType === 'unordered') prefix = '• ';
              else if (listType === 'ordered') { prefix = `${orderNumber}. `; orderNumber++; }
              else prefix = '> ';

              return prefix + cleanLine;
            });
          }

          const newSelectedArea = formattedLines.join('\n');
          const newValue = textBefore + newSelectedArea + textAfter;

          // Calculate cursor position
          const cursorPos = lineStart + newSelectedArea.length;

          // Update value and re-render
          lastValueRef.current = newValue;
          isInternalChangeRef.current = true;
          onChange?.(newValue);

          editor.innerHTML = valueToHtml(newValue, variableLabelMap);

          // Restore cursor
          const domPos = valueOffsetToDom(editor, cursorPos);
          const cursorRange = document.createRange();
          cursorRange.setStart(domPos.node, domPos.offset);
          cursorRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(cursorRange);
        },
      }),
      [variableLabelMap, extractAndNotify, restoreSavedRange],
    );

    const isEmpty = !value;

    return (
      <div className="chip-editor-container" style={{ position: "relative" }}>
        {isEmpty && placeholder && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              pointerEvents: "none",
              color: colors.textWeak,
              ...style,
            }}
          >
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          className={className}
          onInput={handleInput}
          onPaste={handlePaste}
          onCopy={handleCopy}
          onCut={handleCut}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            minHeight: "80px",
            outline: "none",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            lineHeight: "1.6",
            ...style,
          }}
        />
        {/* Drop indicator for drag-and-drop */}
        <div
          ref={dropIndicatorRef}
          className="chip-drop-indicator"
          style={{ display: "none" }}
        />
      </div>
    );
  },
);

VariableChipEditor.displayName = "VariableChipEditor";

export default VariableChipEditor;
